import React, { useState, useRef, useEffect } from 'react';
import { Video, Loader2, AlertCircle } from 'lucide-react';
import styles from './DeviceSelector.module.css';

const CAMERA_STORAGE_KEY = 'user-preferred-camera';

const CameraSelector = ({
    selectedVideoDevice,
    onVideoDeviceChange,
    disabled: propDisabled,
    onCameraToggle,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isCameraEnabled, setIsCameraEnabled] = useState(true);
    const dropdownRef = useRef(null);
    const mediaStreamRef = useRef(null);

    // Initialize camera on mount
    useEffect(() => {
        initializeCamera();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const initializeCamera = async () => {
        try {
            // Get the saved camera device if available
            const savedDeviceId = localStorage.getItem(CAMERA_STORAGE_KEY);
            
            const videoInputs = await requestCameraPermission();

            // Determine which device to use:
            // 1. Component prop (if set)
            // 2. Saved device from localStorage (if available and still exists)
            // 3. First available device
            
            let deviceToUse = selectedVideoDevice;
            
            if (!deviceToUse && savedDeviceId) {
                // Check if the saved device is still available
                const savedDeviceExists = videoInputs.some(device => device.deviceId === savedDeviceId);
                if (savedDeviceExists) {
                    deviceToUse = savedDeviceId;
                }
            }
            
            if (!deviceToUse && videoInputs.length > 0) {
                deviceToUse = videoInputs[0].deviceId;
            }

            if (deviceToUse) {
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: { deviceId: { exact: deviceToUse } }
                });

                mediaStreamRef.current = stream;
                setIsCameraEnabled(true);

                // Save the selected device to localStorage
                localStorage.setItem(CAMERA_STORAGE_KEY, deviceToUse);

                if (deviceToUse !== selectedVideoDevice) {
                    onVideoDeviceChange(deviceToUse);
                }

                onCameraToggle(true);
            }
        } catch (err) {
            console.error('Failed to initialize camera:', err);
            setIsCameraEnabled(false);
            onCameraToggle(false);
        }
    };

    const loadDevices = async () => {
        if (!isCameraEnabled) {
            setDevices([]);
            setLoading(false);
            return [];
        }

        try {
            setLoading(true);
            setError(null);

            await navigator.mediaDevices.getUserMedia({ video: true });
            const deviceList = await navigator.mediaDevices.enumerateDevices();

            const videoInputs = deviceList.filter(device => device.kind === 'videoinput');

            if (videoInputs.length === 0) {
                throw new Error('No camera devices found');
            }

            setDevices(videoInputs);

            // If no device is selected, try to use the saved device or default to the first one
            if (!selectedVideoDevice) {
                const savedDeviceId = localStorage.getItem(CAMERA_STORAGE_KEY);
                const savedDeviceExists = savedDeviceId && videoInputs.some(device => device.deviceId === savedDeviceId);
                
                const deviceToUse = savedDeviceExists ? savedDeviceId : videoInputs[0].deviceId;
                onVideoDeviceChange(deviceToUse);
                localStorage.setItem(CAMERA_STORAGE_KEY, deviceToUse);
            }
        } catch (err) {
            setError(err.message || 'Failed to load camera devices');
            console.error('Error loading camera devices:', err);
        } finally {
            setLoading(false);
        }
    };

    const requestCameraPermission = async () => {
        try {
            setLoading(true);
            setError(null);

            // First, request camera permission
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });

            // If permission granted, enumerate devices
            const deviceList = await navigator.mediaDevices.enumerateDevices();
            const videoInputs = deviceList.filter(device => device.kind === 'videoinput');

            if (videoInputs.length === 0) {
                throw new Error('No camera devices found');
            }

            setDevices(videoInputs);

            // Stop the initial stream
            stream.getTracks().forEach(track => track.stop());

            return videoInputs;
        } catch (err) {
            console.error('Camera permission error:', err);

            // Differentiate between different types of errors
            if (err.name === 'NotAllowedError') {
                setError('Camera access denied. Please check your browser permissions.');
            } else if (err.name === 'NotFoundError') {
                setError('No camera devices found. Please connect a camera.');
            } else {
                setError(err.message || 'Failed to access camera');
            }

            throw err;
        } finally {
            setLoading(false);
        }
    };

    const toggleCamera = async () => {
        try {
            if (isCameraEnabled) {
                // Disable camera
                if (mediaStreamRef.current) {
                    const tracks = mediaStreamRef.current.getTracks();
                    tracks.forEach(track => track.stop());
                    mediaStreamRef.current = null;
                }
                setIsCameraEnabled(false);
                setDevices([]); // Clear devices when camera is disabled
                onVideoDeviceChange(null);
                onCameraToggle(false);
            } else {
                // Try to get camera permission and devices
                const videoInputs = await requestCameraPermission();
                
                // Try to use the saved device or the first available one
                const savedDeviceId = localStorage.getItem(CAMERA_STORAGE_KEY);
                const savedDeviceExists = savedDeviceId && videoInputs.some(device => device.deviceId === savedDeviceId);
                
                const deviceToUse = selectedVideoDevice || (savedDeviceExists ? savedDeviceId : videoInputs[0].deviceId);

                // Get a stream with the selected device
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: { deviceId: { exact: deviceToUse } }
                });

                mediaStreamRef.current = stream;
                setIsCameraEnabled(true);

                // Save selected device to localStorage
                localStorage.setItem(CAMERA_STORAGE_KEY, deviceToUse);

                if (deviceToUse !== selectedVideoDevice) {
                    onVideoDeviceChange(deviceToUse);
                }

                onCameraToggle(true);
            }
        } catch (err) {
            // Error handling is done in requestCameraPermission
            onCameraToggle(false);
            setIsCameraEnabled(false);
            setDevices([]); // Clear devices on error
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleDeviceSelect = (deviceId) => {
        if (isCameraEnabled) {
            // Save the selected device to localStorage
            localStorage.setItem(CAMERA_STORAGE_KEY, deviceId);
            
            onVideoDeviceChange(deviceId);
            setIsOpen(false);
        }
    };

    // eslint-disable-next-line
    const handleArrowClick = (e) => {
        e.stopPropagation();
        if (!propDisabled && !loading && !error) {
            setIsOpen(!isOpen);
        }
    };

    const handleDropdownToggle = (e) => {
        e.stopPropagation();
        if (!propDisabled && !loading && !error) {
            setIsOpen(!isOpen);
        }
    };

    return (
        <div
            className={`${styles.deviceSelector}`}
            ref={dropdownRef}
        >
            <div className={styles.selectorContainer}>
                <div
                    className={`${styles.combinedButton} ${isCameraEnabled ? styles.enabled : styles.disabled} ${propDisabled ? styles.disabled : ''}`}
                >
                    <div
                        className={styles.cameraSection}
                        onClick={toggleCamera}
                        disabled={propDisabled}
                    >
                        {loading ? (
                            <Loader2 className={styles.spinningIcon} />
                        ) : error ? (
                            <AlertCircle className={styles.errorIcon} />
                        ) : (
                            <Video className={styles.icon} />
                        )}
                    </div>

                    <div
                        className={styles.dropdownSection}
                        onClick={handleDropdownToggle}
                    >
                        <span className={`${styles.dropdownArrow} ${isOpen ? styles.open : ''}`}>
                            ▼
                        </span>
                    </div>
                </div>

                {isOpen && !loading && !error && (
                    <div className={`${styles.dropdownContent} ${styles.second}`}>
                        <div className={styles.deviceList}>
                            {devices.map(device => (
                                <div
                                    key={device.deviceId}
                                    className={`${styles.deviceOption} ${selectedVideoDevice === device.deviceId ? styles.selected : ''}`}
                                    onClick={() => handleDeviceSelect(device.deviceId)}
                                >
                                    <div className={styles.deviceInfo}>
                                        <div className={styles.deviceLabel}>{device.label}</div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <button
                            className={styles.refreshButton}
                            onClick={(e) => {
                                e.stopPropagation();
                                loadDevices();
                            }}
                        >
                            Refresh devices
                        </button>
                    </div>
                )}

                {error && (
                    <div className={styles.errorTooltip}>
                        {error}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CameraSelector;